import { useState } from "react";
import { DiagnosticDetailDrawer } from "admin/components/diagnostics/DiagnosticDetailDrawer";
import { useTriageFilters } from "admin/components/diagnostics/TriageFilterContext";
import useDiagnostics from "admin/hooks/useDiagnostics";
import { useTriageStatus } from "admin/hooks/useTriageStatus";
import {
  SEVERITY_CONFIG,
  TRIAGE_STATUS_OPTIONS,
  AGENT_FILTER_OPTIONS,
  SEVERITY_FILTER_OPTIONS,
  TIER_CONFIG,
  TIER_FILTER_OPTIONS,
  getAgentLabel,
  getTriageStatusConfig,
} from "admin/types/constants/diagnostics";
import type {
  DiagnosticListItem,
  DiagnosticTriageStatus,
  RubricDimension,
} from "admin/types/interfaces/diagnostics";
import { formatDiagnosticDate } from "admin/utils/formatting";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { DatePicker } from "~/components/ui/date-picker";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useDebounce } from "~/hooks/useDebounce";
import { cn } from "~/lib/utils";
import { turnAnchorId } from "~/pages/manifold-nextgen/components/conversations/useAnchoredScroll";
import { nextgenAdminTurnPath } from "~/pages/manifold-nextgen/paths";
import { apiMutate } from "~/utils/apiClient";

const PAGE_SIZES = [20, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 400;

function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The backend filters conversation_id by exact UUID, so only send a valid one. */
function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/**
 * The date filters exchange `YYYY-MM-DD` strings with the backend, but the
 * DatePicker works in `Date`. Convert on that boundary in local time so a
 * picked day is not shifted across the day line the way `new Date("2026-08-01")`
 * would (that form parses as UTC midnight).
 */
function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function fromYmd(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? undefined : date;
}

interface SeverityBadgeProps {
  severity: DiagnosticListItem["severity"];
}

const SeverityBadge = ({ severity }: SeverityBadgeProps) => {
  const config = SEVERITY_CONFIG[severity];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        config.badgeClass
      )}
    >
      {config.label}
    </span>
  );
};

interface ScoreDisplayProps {
  score: number | null;
}

const ScoreDisplay = ({ score }: ScoreDisplayProps) => {
  if (score == null) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const colorClass =
    score <= 2
      ? "text-red-600 font-semibold"
      : score <= 3.5
        ? "text-orange-600 font-medium"
        : "text-green-600";

  return <span className={cn("text-sm tabular-nums", colorClass)}>{score.toFixed(1)}</span>;
};

interface TriageStatusCellProps {
  item: DiagnosticListItem;
  onStatusChange: (id: string, newStatus: DiagnosticTriageStatus) => void;
  isUpdating: boolean;
}

const TriageStatusCell = ({ item, onStatusChange, isUpdating }: TriageStatusCellProps) => {
  const config = getTriageStatusConfig(item.triageStatus);

  if (item.triageStatus === "excluded") {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center h-8 text-xs border rounded-md px-2 w-[120px]",
          config.badgeClass
        )}
      >
        {config.label}
      </span>
    );
  }

  return (
    <Select
      value={item.triageStatus}
      onValueChange={(value) => onStatusChange(item.id, value as DiagnosticTriageStatus)}
      disabled={isUpdating}
    >
      <SelectTrigger
        className={cn("h-7 text-xs border px-2 w-[120px]", config.badgeClass)}
        aria-label={`Triage status for turn ${turnAnchorId(item.turnId)}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TRIAGE_STATUS_OPTIONS.filter((opt) => opt.value !== "excluded").map((opt) => (
          <SelectItem key={opt.value} value={opt.value} className="text-xs">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

interface TriageTableProps {
  rubricDimensions: RubricDimension[];
}

export const TriageTable = ({ rubricDimensions }: TriageTableProps) => {
  const {
    agentFilter,
    severityFilter,
    conversationIdSearch: ctxIdSearch,
    dateFilter,
    dateFrom,
    dateTo,
    setAgentFilter,
    setSeverityFilter,
    setConversationIdSearch,
    setDateFrom,
    setDateTo,
    hasHeatmapFilter,
    clearHeatmapFilter,
  } = useTriageFilters();

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(20);
  const [localIdSearch, setLocalIdSearch] = useState(ctxIdSearch);
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { updateTriageStatus, updatingIds } = useTriageStatus();

  const debouncedIdSearch = useDebounce(localIdSearch, SEARCH_DEBOUNCE_MS);

  const offset = (pageNum - 1) * pageSize;

  // Heatmap single-day filter takes precedence over the manual date range.
  const resolvedDateFrom = dateFilter || dateFrom || undefined;
  const resolvedDateTo = dateFilter || dateTo || undefined;

  // The backend filters conversation_id by exact UUID only.
  const trimmedIdSearch = debouncedIdSearch.trim();
  const conversationId = isUuid(trimmedIdSearch) ? trimmedIdSearch : undefined;
  // A non-empty search that is not a UUID: the field goes red, but nothing is sent.
  const hasInvalidIdSearch = trimmedIdSearch !== "" && !conversationId;

  const { items, total, isLoading, error } = useDiagnostics({
    limit: pageSize,
    offset,
    sortKey: "created_at",
    sortOrder: "desc",
    severity: severityFilter !== "all" ? severityFilter : undefined,
    tier: tierFilter !== "all" ? tierFilter : undefined,
    primaryFailureAgent: agentFilter !== "all" ? agentFilter : undefined,
    conversationId,
    dateFrom: resolvedDateFrom,
    dateTo: resolvedDateTo,
  });

  const pageCount = Math.ceil((total ?? 0) / pageSize);
  const start = offset + 1;
  const end = Math.min(offset + pageSize, total ?? 0);

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize) as (typeof PAGE_SIZES)[number]);
    setPageNum(1);
  };

  const handleAgentFilterChange = (value: string) => {
    setAgentFilter(value);
    setPageNum(1);
  };

  const handleSeverityFilterChange = (value: string) => {
    setSeverityFilter(value);
    setPageNum(1);
  };

  const handleTierFilterChange = (value: string) => {
    setTierFilter(value);
    setPageNum(1);
  };

  const handleIdSearchChange = (value: string) => {
    setLocalIdSearch(value);
    setConversationIdSearch(value);
    setPageNum(1);
  };

  const handleIdSearchClear = () => {
    setLocalIdSearch("");
    setConversationIdSearch("");
    setPageNum(1);
  };

  const handleStatusChange = (id: string, newStatus: DiagnosticTriageStatus) => {
    void updateTriageStatus(id, newStatus);
  };

  return (
    <div className="flex flex-col gap-4">
      {hasHeatmapFilter && (
        <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          <span>
            Filtered by heatmap:{" "}
            <span className="font-medium text-foreground">{getAgentLabel(agentFilter)}</span>
            {dateFilter && (
              <>
                {" "}
                on{" "}
                <span className="font-medium text-foreground">
                  {new Date(dateFilter + "T00:00:00").toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </>
            )}
          </span>
          <button
            type="button"
            onClick={clearHeatmapFilter}
            className="ml-auto text-xs underline hover:text-foreground transition-colors cursor-pointer"
          >
            Clear
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Filter by session ID (UUID)…"
            value={localIdSearch}
            onChange={(e) => handleIdSearchChange(e.target.value)}
            className="pl-9 pr-9"
            aria-label="Filter by session ID"
            aria-invalid={hasInvalidIdSearch || undefined}
          />
          {localIdSearch && (
            <button
              type="button"
              onClick={handleIdSearchClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              aria-label="Clear session ID search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Above the field and out of flow: toggling it never resizes the filter
              row, and it clears the table below instead of overlapping it. */}
          {hasInvalidIdSearch && (
            <p className="absolute bottom-full left-0 mb-1 text-xs text-destructive">
              Invalid session ID
            </p>
          )}
        </div>

        <Select value={severityFilter} onValueChange={handleSeverityFilterChange}>
          <SelectTrigger className="w-[160px]" aria-label="Filter by severity">
            <SelectValue placeholder="All Severities" />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={agentFilter} onValueChange={handleAgentFilterChange}>
          <SelectTrigger className="w-[180px]" aria-label="Filter by failure layer">
            <SelectValue placeholder="All Layers" />
          </SelectTrigger>
          <SelectContent>
            {AGENT_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={tierFilter} onValueChange={handleTierFilterChange}>
          <SelectTrigger className="w-[140px]" aria-label="Filter by tier">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            {TIER_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!hasHeatmapFilter && (
          <div className="flex items-center gap-1.5">
            <DatePicker
              withInput
              clearable
              value={fromYmd(dateFrom)}
              onChange={(date) => {
                setDateFrom(date ? toYmd(date) : "");
                setPageNum(1);
              }}
              placeholder="From date"
              aria-label="Filter from date"
              className="w-[168px]"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker
              withInput
              clearable
              value={fromYmd(dateTo)}
              onChange={(date) => {
                setDateTo(date ? toYmd(date) : "");
                setPageNum(1);
              }}
              placeholder="To date"
              aria-label="Filter to date"
              className="w-[168px]"
            />
          </div>
        )}
      </div>

      <div className="rounded-lg border overflow-x-auto">
        {/* N.B. Column widths are sized to their widest real content plus the 24px
            of horizontal cell padding. `TableCell` forces `whitespace-nowrap`, so a
            column narrower than its content does not wrap or ellipsize - the text
            paints straight over the next column and swallows the gutter.

            Every column but Failure Summary is a fixed width, so a container
            narrower than their sum takes it all out of that one column - to 0px,
            where its text lands on top of Status. `min-w` floors the table at those
            fixed widths plus a readable summary and lets the box scroll below it. */}
        <Table className="table-fixed w-full min-w-[1000px] [&_td]:px-3 [&_th]:px-3">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[192px]">Turn ID / Date</TableHead>
              <TableHead className="w-[96px]">Severity</TableHead>
              <TableHead className="w-[104px]">Tier</TableHead>
              <TableHead className="w-[64px]">Score</TableHead>
              <TableHead className="w-[124px]">Failure Layer</TableHead>
              <TableHead>Failure Summary</TableHead>
              <TableHead className="w-[148px]">Status</TableHead>
              <TableHead className="w-[56px] text-center">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {error ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-destructive py-10">
                  Failed to load diagnostics. Please try again.
                </TableCell>
              </TableRow>
            ) : isLoading && !items ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading diagnostics...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                  No diagnostic records found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, idx) => (
                <TableRow
                  key={item.id}
                  className={cn(
                    "hover:bg-muted/40 transition-colors cursor-pointer",
                    drawerOpen && idx === selectedIndex && "bg-muted/60"
                  )}
                  onClick={() => {
                    setSelectedIndex(idx);
                    setDrawerOpen(true);
                  }}
                >
                  <TableCell className="w-[192px]">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      {/* N.B. the full id is rendered and ellipsized by `truncate`
                          rather than sliced in JS. A trace id's first 8 characters
                          are the X-Ray epoch prefix, identical for every turn in
                          the same window, so a fixed slice would read the same
                          down the whole column. */}
                      <span
                        className="font-mono text-xs text-foreground truncate"
                        title={turnAnchorId(item.turnId)}
                      >
                        {turnAnchorId(item.turnId)}
                      </span>
                      {/* Truncates so a longer locale format ellipsizes inside this
                          cell instead of painting over the Severity column. */}
                      <span
                        className="text-xs text-muted-foreground truncate"
                        title={formatDiagnosticDate(item.createdAt)}
                      >
                        {formatDiagnosticDate(item.createdAt)}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="w-[96px]">
                    <SeverityBadge severity={item.severity} />
                  </TableCell>

                  <TableCell className="w-[104px]">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                        TIER_CONFIG[item.tier]?.badgeClass
                      )}
                    >
                      {TIER_CONFIG[item.tier]?.label ?? item.tier}
                    </span>
                  </TableCell>

                  <TableCell className="w-[64px]">
                    <ScoreDisplay score={item.averageScore} />
                  </TableCell>

                  <TableCell className="w-[124px]">
                    <span
                      className="block truncate text-sm text-muted-foreground"
                      title={getAgentLabel(item.primaryFailureAgent)}
                    >
                      {getAgentLabel(item.primaryFailureAgent)}
                    </span>
                  </TableCell>

                  {/* N.B. `whitespace-normal` is required: TableCell's base
                      `whitespace-nowrap` would otherwise keep the summary on one
                      line, so `line-clamp-2` never gets a second line box to clamp
                      and the text is cut mid-word with no ellipsis. */}
                  <TableCell className="whitespace-normal">
                    <p
                      className="text-sm text-muted-foreground line-clamp-2"
                      title={item.failureSummary ?? undefined}
                    >
                      {item.failureSummary ?? "—"}
                    </p>
                  </TableCell>

                  <TableCell className="w-[148px]" onClick={(e) => e.stopPropagation()}>
                    <TriageStatusCell
                      item={item}
                      onStatusChange={handleStatusChange}
                      isUpdating={updatingIds.has(item.id)}
                    />
                  </TableCell>

                  <TableCell className="w-[56px] text-center" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="size-7" asChild>
                      <a
                        href={nextgenAdminTurnPath(item.conversationId, item.turnId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View turn in session (opens in a new tab)"
                        aria-label={`View turn from ${formatDiagnosticDate(item.createdAt)} in session ${truncateId(item.conversationId)}, opens in a new tab`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {total !== undefined && total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {start}–{end} of {total.toLocaleString()} results
          </p>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page:</span>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[80px]" aria-label="Select page size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPageNum(1)}
                disabled={pageNum === 1}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPageNum(pageNum - 1)}
                disabled={pageNum === 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-4 text-sm text-muted-foreground tabular-nums">
                Page {pageNum} of {pageCount || 1}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPageNum(pageNum + 1)}
                disabled={pageNum >= pageCount}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPageNum(pageCount)}
                disabled={pageNum >= pageCount}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <DiagnosticDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        items={items}
        selectedIndex={selectedIndex}
        total={total ?? 0}
        offset={offset}
        rubricDimensions={rubricDimensions}
        onNavigate={(newIndex) => {
          if (newIndex < 0) {
            // Navigate to previous page, select last item
            setPageNum((p) => Math.max(1, p - 1));
            setSelectedIndex(pageSize - 1);
          } else if (newIndex >= items.length) {
            // Navigate to next page, select first item
            setPageNum((p) => Math.min(pageCount, p + 1));
            setSelectedIndex(0);
          } else {
            setSelectedIndex(newIndex);
          }
        }}
      />
    </div>
  );
};
